import { useEffect, useMemo, useState } from "react";
import { supabase } from "../supabaseClient";

const LS_SUIVI = "yoma_suivi_ventes_v1";
const LS_DASH = "yoma_dashboard_promotion_v1";

const GOLD = "#C09A5B";
const TAUPE = "#3D2E20";
const BG = "#F8F4EE";
const BORDER = "#E3D7C8";
const WHITE = "#FFFFFF";
const GREEN = "#5C8A5C";
const ORANGE = "#D68910";
const RED = "#B85C3A";
const GRIS = "#9A8778";

const FF_PLAYFAIR = "'Playfair Display', Georgia, serif";
const FF_DM = "'DM Sans', system-ui, sans-serif";

const DEFAULT_PCTS = [30, 25, 25, 15, 5];

function readSuivi() {
  try {
    const raw = localStorage.getItem(LS_SUIVI);
    if (!raw) return { projects: [], selectedProjectId: null, acquereurs: [] };
    return JSON.parse(raw);
  } catch {
    return { projects: [], selectedProjectId: null, acquereurs: [] };
  }
}

function readDashConfig() {
  try {
    const raw = localStorage.getItem(LS_DASH);
    if (!raw) return { inventoryByProject: {} };
    return JSON.parse(raw);
  } catch {
    return { inventoryByProject: {} };
  }
}

function writeDashConfig(cfg) {
  try {
    localStorage.setItem(LS_DASH, JSON.stringify(cfg));
  } catch {
    /* ignore */
  }
}

function totalEncaisseAcq(a) {
  return (a.versements || []).reduce((s, v) => s + (Number(v.montantVerse) || 0), 0);
}

function pctEncaisse(a) {
  const pv = Number(a.prixVente) || 0;
  if (pv <= 0) return 0;
  return (totalEncaisseAcq(a) / pv) * 100;
}

function estVendu(a) {
  return pctEncaisse(a) >= 99.5;
}

function montantPrevu(prix, pct) {
  const p = Number(prix) || 0;
  const c = Number(pct) || 0;
  return (p * c) / 100;
}

function joursRetard(dateEcheance) {
  if (!dateEcheance) return 0;
  const d = new Date(dateEcheance);
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  const diff = (t - d) / 86400000;
  return diff > 0 ? Math.floor(diff) : 0;
}

function formatMad(n) {
  if (n === "" || n == null || Number.isNaN(Number(n))) return "—";
  return `${Number(n).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })} MAD`;
}

export default function DashboardPromotionPage({ cabinetId = null }) {
  const [cabinet] = useState(() =>
    typeof localStorage !== "undefined" ? localStorage.getItem("yoma_company_name") || "YOMA DESIGN" : "YOMA DESIGN"
  );
  const [suivi, setSuivi] = useState(() => readSuivi());
  const [dashCfg, setDashCfg] = useState(() => readDashConfig());
  const [showCreateProgram, setShowCreateProgram] = useState(false);
  const [newProgram, setNewProgram] = useState({
    name: "",
    code: "",
    location: "",
    total_lots: 0,
    status: "brouillon",
  });
  const [programs, setPrograms] = useState([]);
  const [selectedProgramId, setSelectedProgramId] = useState("");
  const [savingProgram, setSavingProgram] = useState(false);

  async function loadPrograms() {
    const { data, error } = await supabase
      .from("promotion_programs")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) { console.error(error); return; }
    setPrograms(data || []);
  }

  async function handleCreateProgram() {
    if (!newProgram.name.trim()) {
      alert("Le nom du programme est obligatoire.");
      return;
    }
    setSavingProgram(true);
    const payload = {
      name: newProgram.name.trim(),
      code: newProgram.code.trim() || null,
      location: newProgram.location.trim() || null,
      total_lots: Number(newProgram.total_lots) || 0,
      status: newProgram.status || "brouillon",
    };
    const { data, error } = await supabase
      .from("promotion_programs")
      .insert([payload])
      .select()
      .single();
    setSavingProgram(false);
    if (error) {
      console.error(error);
      alert("Erreur lors de l'enregistrement.");
      return;
    }
    setShowCreateProgram(false);
    setNewProgram({ name: "", code: "", location: "", total_lots: 0, status: "brouillon" });
    await loadPrograms();
    if (data?.id) setSelectedProgramId(data.id);
  }

  useEffect(() => {
    const id = "yoma-fonts-dashboard-promo";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700&family=DM+Sans:wght@400;500;600;700&display=swap";
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible") {
        setSuivi(readSuivi());
        setDashCfg(readDashConfig());
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  useEffect(() => { loadPrograms(); }, []);

  useEffect(() => {
    if (!selectedProgramId && programs.length > 0) {
      setSelectedProgramId(String(programs[0].id));
    }
  }, [programs, selectedProgramId]);

  const selectedId = selectedProgramId || "";
  const acquereurs = useMemo(() => {
    if (!selectedId) return [];
    try {
      const raw = localStorage.getItem(`yoma_suivi_ventes_${cabinetId}_${selectedId}`);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [cabinetId, selectedId, suivi]);

  const totalLotsInventaire = useMemo(() => {
    const inv = dashCfg.inventoryByProject?.[selectedId];
    if (typeof inv === "number" && inv > 0) return inv;
    const base = Math.max(acquereurs.length + 8, 24);
    return base;
  }, [dashCfg, selectedId, acquereurs.length]);

  const stats = useMemo(() => {
    const vendus = acquereurs.filter(estVendu).length;
    const occupés = acquereurs.length;
    const réservés = Math.max(0, occupés - vendus);
    const disponibles = Math.max(0, totalLotsInventaire - occupés);

    const caEngagé = acquereurs.reduce((s, a) => s + (Number(a.prixVente) || 0), 0);
    const avg = occupés > 0 ? caEngagé / occupés : 0;
    const caTotalProgramme = caEngagé + avg * disponibles;

    let encaissé = 0;
    acquereurs.forEach((a) => {
      encaissé += totalEncaisseAcq(a);
    });

    const pctCommercialisation = totalLotsInventaire > 0 ? Math.min(100, (occupés / totalLotsInventaire) * 100) : 0;
    const pctFinancier = caTotalProgramme > 0 ? Math.min(100, (encaissé / caTotalProgramme) * 100) : 0;

    return {
      vendus,
      réservés,
      disponibles,
      occupés,
      caEngagé,
      caTotalProgramme,
      encaissé,
      reste: Math.max(0, caTotalProgramme - encaissé),
      pctCommercialisation,
      pctFinancier,
    };
  }, [acquereurs, totalLotsInventaire]);

  const versementsMois = useMemo(() => {
    const out = [];
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const fin = new Date(now);
    fin.setDate(fin.getDate() + 30);

    acquereurs.forEach((a) => {
      (a.versements || []).forEach((v, idx) => {
        if (v.statut === "integral") return;
        if (!v.dateEcheance) return;
        const d = new Date(v.dateEcheance);
        d.setHours(0, 0, 0, 0);
        if (d >= now && d <= fin) {
          const prev = montantPrevu(a.prixVente, v.pct ?? DEFAULT_PCTS[idx]);
          const reste = Math.max(0, prev - (Number(v.montantVerse) || 0));
          if (reste <= 0.01) return;
          out.push({
            id: `${a.id}-${idx}`,
            nom: `${a.prenom || ""} ${a.nom || ""}`.trim() || "—",
            villa: a.numVilla || "—",
            montant: reste,
            date: v.dateEcheance,
            statut: v.statut,
          });
        }
      });
    });
    out.sort((x, y) => new Date(x.date) - new Date(y.date));
    return out;
  }, [acquereurs]);

  const alertes = useMemo(() => {
    const retards = [];
    acquereurs.forEach((a) => {
      (a.versements || []).forEach((v, idx) => {
        if (v.statut === "integral") return;
        const prev = montantPrevu(a.prixVente, v.pct ?? DEFAULT_PCTS[idx]);
        const jr = joursRetard(v.dateEcheance);
        const reste = Math.max(0, prev - (Number(v.montantVerse) || 0));
        if (jr > 30 && reste > 0.01) {
          retards.push(`${a.prenom} ${a.nom} — V${idx + 1} (${jr} j.)`);
        }
      });
    });

    const sansEmail = acquereurs.filter((a) => !String(a.email || "").trim()).map((a) => `${a.prenom} ${a.nom} — Villa ${a.numVilla || "—"}`);

    const contratsNonSignés = acquereurs
      .filter((a) => totalEncaisseAcq(a) <= 0 && (a.versements || []).every((v) => v.statut === "a_venir" || !v.dateVersement))
      .map((a) => `${a.prenom} ${a.nom} — Villa ${a.numVilla || "—"}`);

    return { retards, sansEmail, contratsNonSignés };
  }, [acquereurs]);

  const cardStyle = {
    background: WHITE,
    border: `1px solid ${BORDER}`,
    borderRadius: 12,
    padding: 16,
  };

  const maxBar = Math.max(stats.vendus + stats.réservés + stats.disponibles, 1);

  if (cabinetId == null) {
    return <div style={{ padding: 24, textAlign: "center", color: TAUPE }}>Chargement...</div>;
  }

  return (
    <main style={{ minHeight: "100vh", background: BG, padding: 24, fontFamily: FF_DM, color: TAUPE }}>
      <header style={{ marginBottom: 22 }}>
        <div style={{ fontSize: 12, color: "#8B7361", marginBottom: 6 }}>{cabinet}</div>
        <h1 style={{ margin: 0, fontFamily: FF_PLAYFAIR, fontSize: 32, fontWeight: 700 }}>Dashboard Promotion</h1>
        <p style={{ margin: "8px 0 0", fontSize: 14, color: "#8B7361" }}>Vue globale du programme immobilier</p>
        <div style={{ marginTop: 12 }}>
          <button
            onClick={() => setShowCreateProgram(true)}
            style={{
              padding: "10px 16px",
              border: "none",
              borderRadius: 8,
              background: GOLD,
              color: WHITE,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            + Nouveau programme
          </button>
        </div>
        <div style={{ marginTop: 14, maxWidth: 400 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: "#8B7361" }}>Programme / projet</label>
          <select
            value={selectedProgramId}
            onChange={(e) => setSelectedProgramId(e.target.value)}
            style={{
              padding: "10px 14px",
              border: "1px solid #DDD0C0",
              borderRadius: 6,
              fontSize: 14,
              minWidth: 250,
            }}
          >
            <option value="">— Sélectionner un programme —</option>
            {programs.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <p style={{ fontSize: 11, color: "#8B7361", marginTop: 8, lineHeight: 1.4 }}>
            Inventaire total lots (calibrage dashboard) :{" "}
            <input
              type="number"
              min={1}
              value={dashCfg.inventoryByProject?.[selectedId] ?? totalLotsInventaire}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10) || 24;
                const next = {
                  ...dashCfg,
                  inventoryByProject: { ...dashCfg.inventoryByProject, [selectedId]: n },
                };
                setDashCfg(next);
                writeDashConfig(next);
              }}
              style={{ width: 72, padding: 4, borderRadius: 6, border: `1px solid ${BORDER}`, marginLeft: 6 }}
            />{" "}
            (sauvegardé localement pour ce programme)
          </p>
        </div>
      </header>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 12, marginBottom: 20 }}>
        <div style={cardStyle}>
          <div style={{ fontSize: 11, color: "#8B7361", marginBottom: 6 }}>Total villas / lots</div>
          <div style={{ fontFamily: FF_PLAYFAIR, fontSize: 26, fontWeight: 700 }}>{totalLotsInventaire}</div>
        </div>
        <div style={cardStyle}>
          <div style={{ fontSize: 11, color: "#8B7361", marginBottom: 6 }}>Vendus</div>
          <div style={{ fontFamily: FF_PLAYFAIR, fontSize: 26, fontWeight: 700, color: GREEN }}>{stats.vendus}</div>
        </div>
        <div style={cardStyle}>
          <div style={{ fontSize: 11, color: "#8B7361", marginBottom: 6 }}>Disponibles</div>
          <div style={{ fontFamily: FF_PLAYFAIR, fontSize: 26, fontWeight: 700, color: GOLD }}>{stats.disponibles}</div>
        </div>
        <div style={cardStyle}>
          <div style={{ fontSize: 11, color: "#8B7361", marginBottom: 6 }}>CA total programme</div>
          <div style={{ fontFamily: FF_PLAYFAIR, fontSize: 22, fontWeight: 700, color: GOLD }}>{formatMad(stats.caTotalProgramme)}</div>
        </div>
        <div style={cardStyle}>
          <div style={{ fontSize: 11, color: "#8B7361", marginBottom: 6 }}>Encaissé à ce jour</div>
          <div style={{ fontFamily: FF_PLAYFAIR, fontSize: 22, fontWeight: 700, color: GREEN }}>{formatMad(stats.encaissé)}</div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 16, marginBottom: 20 }}>
        <div style={cardStyle}>
          <h2 style={{ margin: "0 0 14px", fontFamily: FF_PLAYFAIR, fontSize: 20 }}>État des ventes</h2>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 12, height: 140, marginBottom: 16 }}>
            {[
              { label: "Vendus", val: stats.vendus, color: GREEN },
              { label: "Réservés", val: stats.réservés, color: ORANGE },
              { label: "Disponibles", val: stats.disponibles, color: GOLD },
            ].map((b) => (
              <div key={b.label} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                <div
                  style={{
                    width: "100%",
                    maxWidth: 80,
                    height: `${Math.max(8, (b.val / maxBar) * 120)}px`,
                    background: b.color,
                    borderRadius: 8,
                    opacity: 0.85,
                  }}
                />
                <span style={{ fontSize: 11, fontWeight: 700, color: "#8B7361" }}>{b.label}</span>
                <span style={{ fontSize: 16, fontWeight: 700 }}>{b.val}</span>
              </div>
            ))}
          </div>
          <div style={{ marginBottom: 8, fontSize: 13, color: "#8B7361" }}>
            Commercialisation : <strong style={{ color: TAUPE }}>{stats.pctCommercialisation.toFixed(1)} %</strong> des lots
            engagés ou vendus
          </div>
          <div style={{ height: 10, background: "#EFE4D4", borderRadius: 999, overflow: "hidden" }}>
            <div style={{ width: `${stats.pctCommercialisation}%`, height: "100%", background: GOLD }} />
          </div>
        </div>

        <div style={cardStyle}>
          <h2 style={{ margin: "0 0 14px", fontFamily: FF_PLAYFAIR, fontSize: 20 }}>État financier</h2>
          <div style={{ fontSize: 14, marginBottom: 8 }}>
            CA total attendu : <strong>{formatMad(stats.caTotalProgramme)}</strong>
          </div>
          <div style={{ fontSize: 14, marginBottom: 8, color: GREEN }}>
            Encaissé à ce jour : <strong>{formatMad(stats.encaissé)}</strong>
          </div>
          <div style={{ fontSize: 14, marginBottom: 14 }}>
            Reste à encaisser : <strong>{formatMad(stats.reste)}</strong>
          </div>
          <div style={{ height: 10, background: "#EFE4D4", borderRadius: 999, overflow: "hidden" }}>
            <div style={{ width: `${stats.pctFinancier}%`, height: "100%", background: GREEN }} />
          </div>
          <div style={{ fontSize: 11, color: "#8B7361", marginTop: 6 }}>{stats.pctFinancier.toFixed(1)} % du CA programme</div>
        </div>
      </div>

      <div style={{ ...cardStyle, marginBottom: 20 }}>
        <h2 style={{ margin: "0 0 12px", fontFamily: FF_PLAYFAIR, fontSize: 20 }}>Versements attendus ce mois</h2>
        <p style={{ fontSize: 12, color: "#8B7361", marginTop: 0 }}>Échéances dans les 30 prochains jours (montant restant dû)</p>
        {versementsMois.length === 0 ? (
          <div style={{ padding: 16, color: "#8B7361" }}>Aucun versement à échéance dans cette fenêtre.</div>
        ) : (
          <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 10 }}>
            {versementsMois.map((v) => (
              <li
                key={v.id}
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 10,
                  padding: "12px 14px",
                  background: "#FCFAF7",
                  borderRadius: 10,
                  border: `1px solid ${BORDER}`,
                }}
              >
                <span>
                  <strong>{v.nom}</strong> — Villa {v.villa}
                </span>
                <span style={{ fontFamily: FF_PLAYFAIR, fontSize: 18, fontWeight: 700, color: GOLD }}>{formatMad(v.montant)}</span>
                <span style={{ fontSize: 13, color: "#8B7361" }}>{new Date(v.date).toLocaleDateString("fr-FR")}</span>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    padding: "4px 10px",
                    borderRadius: 999,
                    background: `${GRIS}22`,
                    color: TAUPE,
                  }}
                >
                  {v.statut}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div style={{ ...cardStyle, marginBottom: 20 }}>
        <h2 style={{ margin: "0 0 12px", fontFamily: FF_PLAYFAIR, fontSize: 20 }}>Points d&apos;attention</h2>
        <ul style={{ margin: 0, paddingLeft: 18, color: TAUPE, lineHeight: 1.7, fontSize: 13 }}>
          <li>
            <strong style={{ color: RED }}>Versements en retard &gt; 30 jours :</strong>{" "}
            {alertes.retards.length ? alertes.retards.join(" · ") : "Aucun"}
          </li>
          <li>
            <strong style={{ color: ORANGE }}>Acquéreurs sans email :</strong>{" "}
            {alertes.sansEmail.length ? alertes.sansEmail.join(" · ") : "Aucun"}
          </li>
          <li>
            <strong style={{ color: "#8B7361" }}>Contrats / dossiers non engagés financièrement :</strong>{" "}
            {alertes.contratsNonSignés.length ? alertes.contratsNonSignés.join(" · ") : "Aucun"}
          </li>
        </ul>
      </div>

      <div style={cardStyle}>
        <h2 style={{ margin: "0 0 12px", fontFamily: FF_PLAYFAIR, fontSize: 20 }}>Liste des villas (acquéreurs)</h2>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", minWidth: 640, borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#FCFAF7", textAlign: "left" }}>
                <th style={th}>N° Villa</th>
                <th style={th}>Acquéreur</th>
                <th style={th}>Prix</th>
                <th style={th}>% Encaissé</th>
                <th style={th}>Statut</th>
              </tr>
            </thead>
            <tbody>
              {acquereurs.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ ...td, color: "#8B7361" }}>
                    Aucun acquéreur — renseignez le suivi des ventes.
                  </td>
                </tr>
              ) : (
                acquereurs.map((a) => {
                  const pct = pctEncaisse(a);
                  let statut = "Réservé";
                  if (estVendu(a)) statut = "Vendu";
                  else if (pct < 0.5) statut = "En cours";
                  return (
                    <tr key={a.id}>
                      <td style={td}>{a.numVilla || "—"}</td>
                      <td style={td}>
                        {(a.prenom || "") + " " + (a.nom || "") || "—"}
                      </td>
                      <td style={td}>{formatMad(a.prixVente)}</td>
                      <td style={td}>
                        <div style={{ height: 6, background: "#EFE4D4", borderRadius: 999, maxWidth: 120 }}>
                          <div style={{ width: `${Math.min(100, pct)}%`, height: "100%", background: GREEN, borderRadius: 999 }} />
                        </div>
                        <span style={{ fontSize: 11 }}>{pct.toFixed(1)} %</span>
                      </td>
                      <td style={td}>
                        <span
                          style={{
                            padding: "4px 10px",
                            borderRadius: 999,
                            fontSize: 11,
                            fontWeight: 700,
                            background:
                              statut === "Vendu" ? "rgba(92,138,92,0.15)" : statut === "Réservé" ? "rgba(192,154,91,0.2)" : "rgba(154,135,120,0.15)",
                            color: statut === "Vendu" ? GREEN : statut === "Réservé" ? GOLD : GRIS,
                          }}
                        >
                          {statut}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {stats.disponibles > 0 && (
          <p style={{ margin: "12px 0 0", fontSize: 12, color: "#8B7361" }}>
            + {stats.disponibles} lot(s) disponible(s) sur l&apos;inventaire total ({totalLotsInventaire} lots).
          </p>
        )}
      </div>

      {showCreateProgram && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex",
          alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#F8F4EE", borderRadius: 12, padding: 32,
            width: 480, boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
            <h3 style={{ fontFamily: "Playfair Display, serif", fontSize: 22,
              color: "#3D2E20", marginBottom: 24 }}>Nouveau programme</h3>
            <input type="text" placeholder="Nom du programme *"
              value={newProgram.name}
              onChange={(e) => setNewProgram({ ...newProgram, name: e.target.value })}
              style={{ width: "100%", padding: "10px 14px", marginBottom: 12,
                border: "1px solid #DDD0C0", borderRadius: 6, fontSize: 14 }} />
            <input type="text" placeholder="Code programme"
              value={newProgram.code}
              onChange={(e) => setNewProgram({ ...newProgram, code: e.target.value })}
              style={{ width: "100%", padding: "10px 14px", marginBottom: 12,
                border: "1px solid #DDD0C0", borderRadius: 6, fontSize: 14 }} />
            <input type="text" placeholder="Localisation"
              value={newProgram.location}
              onChange={(e) => setNewProgram({ ...newProgram, location: e.target.value })}
              style={{ width: "100%", padding: "10px 14px", marginBottom: 12,
                border: "1px solid #DDD0C0", borderRadius: 6, fontSize: 14 }} />
            <input type="number" placeholder="Nombre de lots"
              value={newProgram.total_lots}
              onChange={(e) => setNewProgram({ ...newProgram, total_lots: e.target.value })}
              style={{ width: "100%", padding: "10px 14px", marginBottom: 12,
                border: "1px solid #DDD0C0", borderRadius: 6, fontSize: 14 }} />
            <select value={newProgram.status}
              onChange={(e) => setNewProgram({ ...newProgram, status: e.target.value })}
              style={{ width: "100%", padding: "10px 14px", marginBottom: 24,
                border: "1px solid #DDD0C0", borderRadius: 6, fontSize: 14 }}>
              <option value="brouillon">Brouillon</option>
              <option value="actif">Actif</option>
              <option value="termine">Terminé</option>
            </select>
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button onClick={() => setShowCreateProgram(false)}
                style={{ padding: "10px 20px", border: "1px solid #DDD0C0",
                  borderRadius: 6, background: "white", cursor: "pointer" }}>
                Annuler
              </button>
              <button onClick={handleCreateProgram} disabled={savingProgram}
                style={{ padding: "10px 20px", background: "#C09A5B",
                  color: "white", border: "none", borderRadius: 6,
                  cursor: "pointer", fontWeight: 600 }}>
                {savingProgram ? "Enregistrement..." : "Enregistrer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

const th = { padding: "10px 12px", borderBottom: `2px solid ${BORDER}`, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "#8B7361" };
const td = { padding: "10px 12px", borderBottom: `1px solid ${BORDER}` };
